/**
 * TENSION Notification Service
 * 
 * Core notification engine that handles:
 * - Daily check-in reminders with adaptive timing
 * - Streak maintenance notifications
 * - Partner coordination alerts
 * - RTI state-based notifications
 * - Smart scheduling with quiet hours
 */

import {
  trackNotificationDelivered,
  trackNotificationClicked,
  trackNotificationsEnabled,
  trackNotificationsDisabled,
  trackReminderTimeChanged,
} from './notification-analytics';

export interface NotificationSettings {
  enabled: boolean;
  dailyReminder: boolean;
  reminderTime: string; // HH:MM format
  partnerSync: boolean;
  streakReminders: boolean;
  highTensionAlerts: boolean;
  weeklyReport: boolean;
  actionReminders: boolean;
  microActionReminders: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string; // HH:MM format
  quietHoursEnd: string; // HH:MM format
  adaptiveTiming: boolean; // Learn optimal reminder time
  forecastAlerts: boolean; // Premium feature
}

export interface NotificationData {
  id: string;
  type: 'checkin' | 'streak' | 'partner' | 'tension' | 'weekly' | 'action' | 'micro';
  title: string;
  body: string;
  priority: 'low' | 'medium' | 'high';
  scheduledFor: number; // timestamp
  delivered: boolean;
  deliveredAt?: number;
  actionUrl?: string;
  metadata?: Record<string, any>;
}

interface NotificationOptions {
  tag?: string;
  icon?: string;
  badge?: string;
  requireInteraction?: boolean;
  actionUrl?: string;
  data?: Record<string, any>;
}

const STORAGE_KEYS = {
  SETTINGS: 'tension_notification_settings',
  QUEUE: 'tension_notification_queue',
  HISTORY: 'tension_notification_history',
  LAST_CHECKIN: 'tension_last_checkin_time',
  PERMISSION: 'tension_notification_permission',
  OPTIMAL_TIME: 'tension_optimal_reminder_time',
} as const;

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  dailyReminder: true,
  reminderTime: '19:00', // 7 PM default
  partnerSync: false,
  streakReminders: true,
  highTensionAlerts: true,
  weeklyReport: true,
  actionReminders: true,
  microActionReminders: true,
  quietHoursEnabled: true,
  quietHoursStart: '22:00', // 10 PM
  quietHoursEnd: '08:00', // 8 AM
  adaptiveTiming: false,
  forecastAlerts: false, // Premium only
};

/**
 * Get notification settings
 */
export function getNotificationSettings(): NotificationSettings {
  const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
  return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
}

/**
 * Update notification settings
 */
export function updateNotificationSettings(updates: Partial<NotificationSettings>): void {
  const current = getNotificationSettings();
  const updated = { ...current, ...updates };
  localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(updated));

  if (updates.enabled !== undefined) {
    if (updates.enabled) {
      trackNotificationsEnabled(updated.reminderTime);
    } else {
      trackNotificationsDisabled();
    }
  }
  if (
    updates.reminderTime !== undefined &&
    updates.reminderTime !== current.reminderTime
  ) {
    trackReminderTimeChanged(current.reminderTime, updates.reminderTime);
  }

  if (updates.dailyReminder !== undefined || updates.reminderTime !== undefined) {
    rescheduleNotifications();
  }
}

/**
 * Check if notifications are enabled
 */
export function areNotificationsEnabled(): boolean {
  if (!('Notification' in window)) {
    return false;
  }
  
  try {
    if (Notification.permission !== 'granted') {
      return false;
    }
    
    const settings = getNotificationSettings();
    return settings.enabled;
  } catch (error) {
    return false;
  }
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.log('Notifications not supported on this device');
    return false;
  }
  
  try {
    const permission = await Notification.requestPermission();
    localStorage.setItem(STORAGE_KEYS.PERMISSION, permission);
    
    if (permission === 'granted') {
      updateNotificationSettings({ enabled: true });
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

/**
 * Check if currently in quiet hours
 */
function isInQuietHours(): boolean {
  const settings = getNotificationSettings();
  if (!settings.quietHoursEnabled) {
    return false;
  }
  
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  
  const [startHour, startMin] = settings.quietHoursStart.split(':').map(Number);
  const [endHour, endMin] = settings.quietHoursEnd.split(':').map(Number);
  
  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;
  
  // Handle overnight quiet hours (e.g., 22:00 to 08:00)
  if (startTime > endTime) {
    return currentTime >= startTime || currentTime < endTime;
  }
  
  return currentTime >= startTime && currentTime < endTime;
}

/**
 * Show a notification
 */
export function showNotification(title: string, body: string, options?: NotificationOptions): void {
  // Check quiet hours
  if (isInQuietHours()) {
    console.log('Quiet hours active, deferring notification:', title);
    return;
  }

  // DEFENSIVE: Wrap all Notification usage in try-catch
  try {
    const notification = new Notification(title, {
      body,
      icon: options?.icon || '/icons/icon-192x192.png',
      badge: options?.badge || '/icons/icon-72x72.png',
      tag: options?.tag,
      requireInteraction: options?.requireInteraction || false,
      data: options?.data,
    });

    notification.onclick = () => {
      window.focus();
      if (options?.actionUrl) {
        window.location.href = options.actionUrl;
      }
      notification.close();
      trackNotificationClicked(options?.tag || 'unknown');
    };
    
    trackNotificationDelivered(options?.tag || 'unknown');
  } catch (error) {
    // Silently fail if Notification API is broken
    console.debug('Notification API error:', error);
  }
}

/**
 * Schedule a notification
 */
export function scheduleNotification(notification: NotificationData): void {
  const queue = getNotificationQueue();
  queue.push(notification);
  localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify(queue));
}

/**
 * Get notification queue
 */
function getNotificationQueue(): NotificationData[] {
  const stored = localStorage.getItem(STORAGE_KEYS.QUEUE);
  return stored ? JSON.parse(stored) : [];
}

/**
 * Process notification queue
 */
function processNotificationQueue(): void {
  const queue = getNotificationQueue();
  const now = Date.now();
  
  const toDeliver: NotificationData[] = [];
  const remaining: NotificationData[] = [];
  
  queue.forEach(notification => {
    if (notification.scheduledFor <= now && !notification.delivered) {
      toDeliver.push(notification);
    } else if (!notification.delivered) {
      remaining.push(notification);
    }
  });
  
  // Deliver notifications
  toDeliver.forEach(notification => {
    showNotification(notification.title, notification.body, {
      tag: notification.id,
      actionUrl: notification.actionUrl,
      requireInteraction: notification.priority === 'high',
      data: notification.metadata,
    });
    
    notification.delivered = true;
    notification.deliveredAt = now;
    
    // Add to history
    addToHistory(notification);
  });
  
  // Update queue
  localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify(remaining));
}

/**
 * Add notification to history
 */
function addToHistory(notification: NotificationData): void {
  const history = getNotificationHistory();
  history.unshift(notification);
  
  // Keep last 100 notifications
  if (history.length > 100) {
    history.splice(100);
  }
  
  localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
}

/**
 * Get notification history
 */
export function getNotificationHistory(): NotificationData[] {
  const stored = localStorage.getItem(STORAGE_KEYS.HISTORY);
  return stored ? JSON.parse(stored) : [];
}

/**
 * Send daily check-in reminder
 */
export function scheduleDailyCheckInReminder(): void {
  const settings = getNotificationSettings();
  
  if (!settings.dailyReminder || !areNotificationsEnabled()) {
    return;
  }
  
  // Parse reminder time
  const [hours, minutes] = settings.reminderTime.split(':').map(Number);
  
  // Calculate next reminder time
  const now = new Date();
  const reminderTime = new Date();
  reminderTime.setHours(hours, minutes, 0, 0);
  
  // If time has passed today, schedule for tomorrow
  if (reminderTime.getTime() < now.getTime()) {
    reminderTime.setDate(reminderTime.getDate() + 1);
  }

  const getReminderBody = (): string => {
    try {
      const raw = localStorage.getItem('tension_checkins');
      const checkins = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(checkins) || checkins.length === 0) {
        return '10-second check-in: capture today\'s pulse and start building momentum.';
      }

      const latest = checkins[checkins.length - 1];
      const score = typeof latest?.rtiScore === 'number' ? latest.rtiScore : null;
      const lastTs = latest?.timestamp || latest?.date;
      const lastDate = lastTs ? new Date(lastTs) : null;
      const daysSince =
        lastDate && !Number.isNaN(lastDate.getTime())
          ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24))
          : 0;

      if (daysSince >= 2) {
        return 'Quick catch-up is ready. One check-in gets you back on track today.';
      }
      if (score !== null && score >= 61) {
        return 'Gentle nudge: tension looked elevated recently. A short check-in can help steady the next 24 hours.';
      }
      if (score !== null && score <= 35) {
        return 'You are in a stable zone. Check in to maintain momentum and reinforce your streak.';
      }
      return 'Daily pulse check: one minute now can prevent hours of overthinking later.';
    } catch {
      return 'Time to check in and track your relationship tension.';
    }
  };
  
  scheduleNotification({
    id: `checkin-${reminderTime.getTime()}`,
    type: 'checkin',
    title: 'Daily Check-In',
    body: getReminderBody(),
    priority: 'medium',
    scheduledFor: reminderTime.getTime(),
    delivered: false,
    actionUrl: '/checkin',
  });
}

/**
 * Send streak reminder
 */
export function sendStreakReminder(streak: number): void {
  if (!areNotificationsEnabled()) {
    return;
  }
  
  const settings = getNotificationSettings();
  if (!settings.streakReminders) {
    return;
  }
  
  let message = '';
  
  if (streak === 3) {
    message = '3-day streak! You\'re building awareness.';
  } else if (streak === 7) {
    message = '7-day streak! One week of consistency.';
  } else if (streak === 14) {
    message = '2-week streak! Patterns are becoming clearer.';
  } else if (streak === 30) {
    message = '30-day streak! You\'re committed to growth.';
  } else if (streak === 60) {
    message = '60-day streak! Your awareness has deepened.';
  } else if (streak === 90) {
    message = '90-day streak! You\'ve built a lasting practice.';
  } else if (streak === 100) {
    message = '100-day streak! This is incredible dedication.';
  }
  
  if (message) {
    showNotification('🔥 Streak Milestone!', message, {
      tag: `streak-${streak}`,
      actionUrl: '/home',
    });
  }
}

/**
 * Send partner check-in notification
 */
export function sendPartnerCheckInNotification(partnerName: string): void {
  if (!areNotificationsEnabled()) {
    return;
  }
  
  const settings = getNotificationSettings();
  if (!settings.partnerSync) {
    return;
  }
  
  showNotification(
    'Partner Check-In',
    `${partnerName} just completed their check-in. View their update.`,
    {
      tag: 'partner-checkin',
      actionUrl: '/partner',
      requireInteraction: true,
    }
  );
}

/**
 * Send high tension alert
 */
export function sendHighTensionAlert(rtiScore: number): void {
  if (!areNotificationsEnabled()) {
    return;
  }
  
  const settings = getNotificationSettings();
  if (!settings.highTensionAlerts) {
    return;
  }
  
  showNotification(
    'High Tension Detected',
    `Your RTI is ${rtiScore}. Consider taking a grounding action.`,
    {
      tag: 'high-tension',
      actionUrl: '/actions',
      requireInteraction: true,
    }
  );
}

/**
 * Schedule weekly report notification
 */
function scheduleWeeklyReport(): void {
  const settings = getNotificationSettings();
  
  if (!settings.weeklyReport || !areNotificationsEnabled()) {
    return;
  }
  
  // Schedule for Sunday at 10am
  const now = new Date();
  const nextSunday = new Date();
  const daysUntilSunday = (7 - now.getDay()) % 7;
  
  nextSunday.setDate(now.getDate() + (daysUntilSunday || 7));
  nextSunday.setHours(10, 0, 0, 0);
  
  // If it's Sunday and past 10am, schedule for next Sunday
  if (daysUntilSunday === 7 && now.getHours() >= 10) {
    nextSunday.setDate(nextSunday.getDate() + 7);
  }
  
  scheduleNotification({
    id: `weekly-${Date.now()}`,
    type: 'weekly',
    title: 'Weekly Relationship Summary',
    body: 'Your weekly patterns and insights are ready to review.',
    priority: 'medium',
    scheduledFor: nextSunday.getTime(),
    delivered: false,
    actionUrl: '/weekly-summary',
  });
}

/**
 * Learn optimal reminder time based on check-in patterns
 */
export function learnOptimalReminderTime(): void {
  const checkIns = localStorage.getItem('tension_checkins');
  if (!checkIns) return;
  
  const parsedCheckIns = JSON.parse(checkIns);
  
  // Find most common check-in hour
  const hours = parsedCheckIns.map((c: any) => new Date(c.timestamp).getHours());
  const hourCounts: Record<number, number> = {};
  
  hours.forEach((hour: number) => {
    hourCounts[hour] = (hourCounts[hour] || 0) + 1;
  });
  
  let maxCount = 0;
  let optimalHour = 19; // Default 7 PM
  
  Object.entries(hourCounts).forEach(([hour, count]) => {
    if (count > maxCount) {
      maxCount = count;
      optimalHour = parseInt(hour);
    }
  });
  
  // Update optimal time
  const optimalTime = `${optimalHour.toString().padStart(2, '0')}:00`;
  localStorage.setItem(STORAGE_KEYS.OPTIMAL_TIME, optimalTime);
  
  // Suggest to user if different from current
  const currentSettings = getNotificationSettings();
  if (currentSettings.adaptiveTiming && currentSettings.reminderTime !== optimalTime) {
    updateNotificationSettings({ reminderTime: optimalTime });
  }
}

/**
 * Reschedule all notifications
 */
export function rescheduleNotifications(): void {
  // Clear existing queue
  localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify([]));
  
  // Reschedule daily check-in
  scheduleDailyCheckInReminder();
  
  // Reschedule weekly report
  scheduleWeeklyReport();
}

/**
 * Test notification (for settings page)
 */
export function sendTestNotification(): void {
  showNotification(
    'TENSION Notifications Enabled',
    'You\'ll receive gentle reminders to maintain your check-in streak and stay aware of tension patterns.',
    {
      tag: 'test',
      actionUrl: '/home',
    }
  );
}

/**
 * Initialize notification service
 */
export function initNotificationService(): void {
  console.log('🔔 Initializing notification service...');
  
  // Check if Notification API is available (not available on iOS Safari)
  if (!('Notification' in window)) {
    console.log('⚠️ Notification API not available on this device (iOS Safari doesn\'t support it)');
    console.log('✅ Notification service initialized (disabled mode)');
    return;
  }
  
  // DEFENSIVE: Check if Notification is actually usable
  try {
    // Try to access Notification.permission safely
    const permission = window.Notification?.permission || 'denied';
    localStorage.setItem(STORAGE_KEYS.PERMISSION, permission);
  } catch (e) {
    console.warn('⚠️ Notification API exists but is not accessible:', e);
    console.log('✅ Notification service initialized (disabled mode)');
    return;
  }
  
  // Process queue every minute
  setInterval(processNotificationQueue, 60 * 1000);
  
  // Process queue immediately
  processNotificationQueue();
  
  // Learn optimal timing daily
  if (getNotificationSettings().adaptiveTiming) {
    setInterval(learnOptimalReminderTime, 24 * 60 * 60 * 1000);
  }
  
  // Reschedule notifications if needed
  const queue = getNotificationQueue();
  if (queue.length === 0 && areNotificationsEnabled()) {
    rescheduleNotifications();
  }
  
  console.log('✅ Notification service initialized');
}